import { useCallback, useEffect, useRef, useState } from "react";
import { manualSpinStop, rgsPlay } from "../State/Slices/spinSlice";
import { useDispatch, useSelector } from "react-redux";
import { Settings as Setting } from "../settings";
import {
  decrementRemainingAutoSpins,
  stopAutoPlay,
} from "../State/Slices/settingsSlice";
import { setCounterAnimationFinished } from "../State/Slices/spinSlice";
import { playSound } from "../State/Slices/soundsSlice";
import { SOUND_NAME } from "../sounds";
import { openModal } from "../State/Slices/modalSlice";
import { resetAnimationsState } from "../State/Slices/animationsSlice";
import { resetFreeSpinsState } from "../State/Slices/freeSpinsSlice";

const useReelsController = () => {
  const dispatch = useDispatch();
  const { autoSpinCount, autoPlayActive, remainingAutoSpins } = useSelector(
    (state) => state.settings
  );
  const {
    isSpinning,
    manualStop,
    spinComplete,
    counterAnimationFinished,
    linesRotationFinished,
  } = useSelector((state) => state.spin);
  const { balance, bet, currentRound } = useSelector((state) => state.game);
  const { isFreeSpinMode } = useSelector((state) => state.freeSpins);
  const { freeSpinOutroAnimationCompleted } = useSelector(
    (state) => state.animations
  );

  const reelRefs = useRef([]);
  const timeoutsRef = useRef([]);
  const [stopTriggered, setStopTriggered] = useState(false);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const handleSpin = useCallback(() => {
    if (isFreeSpinMode) {
      return;
    }

    dispatch(setCounterAnimationFinished(false));
    clearAllTimeouts();
    setStopTriggered(false);
    dispatch(rgsPlay());
    reelRefs.current.forEach((reel, index) => {
      reel?.startSpin();
    });
  }, [dispatch, clearAllTimeouts, isFreeSpinMode]);

  const stopReels = useCallback(
    (delay) => {
      if (isFreeSpinMode) {
        return;
      }

      clearAllTimeouts();

      reelRefs.current.forEach((reel, index) => {
        const timeoutId = setTimeout(() => {
          reel?.stopSpin();
        }, delay * index);
        timeoutsRef.current.push(timeoutId);
      });
    },
    [clearAllTimeouts, isFreeSpinMode]
  );

  useEffect(() => {
    if (isFreeSpinMode) {
      return;
    }

    if (autoPlayActive && spinComplete) {
      if (balance < bet) {
        dispatch(stopAutoPlay());
        dispatch(playSound(SOUND_NAME.noCredits));
        dispatch(openModal("credit"));
      } else if (remainingAutoSpins > 0) {
        if (!currentRound?.win) {
          const timeoutId = setTimeout(() => {
            dispatch(decrementRemainingAutoSpins());
            handleSpin();
          }, 600);
          return () => clearTimeout(timeoutId);
        } else if (counterAnimationFinished && linesRotationFinished) {
          dispatch(decrementRemainingAutoSpins());
          handleSpin();
        }
      } else {
        dispatch(stopAutoPlay());
      }
    }
  }, [
    spinComplete,
    autoPlayActive,
    remainingAutoSpins,
    handleSpin,
    autoSpinCount,
    dispatch,
    counterAnimationFinished,
    linesRotationFinished,
    balance,
    bet,
    currentRound,
    isFreeSpinMode,
  ]);

  useEffect(() => {
    if (isFreeSpinMode) {
      return;
    }

    if (autoPlayActive) {
      if (remainingAutoSpins === autoSpinCount) {
        dispatch(decrementRemainingAutoSpins());
        handleSpin();
      }
    }
  }, [
    autoPlayActive,
    autoSpinCount,
    remainingAutoSpins,
    dispatch,
    handleSpin,
    isFreeSpinMode,
  ]);

  const handleManualStop = useCallback(() => {
    if (isFreeSpinMode) {
      return;
    }

    if (stopTriggered) {
      return;
    }

    setStopTriggered(true);
    dispatch(manualSpinStop());
    dispatch(stopAutoPlay());
    stopReels(Setting.STOP_DELAY_MANUAL);
  }, [dispatch, stopReels, stopTriggered, isFreeSpinMode]);

  useEffect(() => {
    let timeoutId;

    if (isFreeSpinMode) {
      return;
    }

    if (isSpinning && !manualStop && !spinComplete) {
      timeoutId = setTimeout(() => {
        stopReels(Setting.STOP_DELAY_AUTO);
      }, Setting.AUTO_STOP_TIMEOUT);
      timeoutsRef.current.push(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [isSpinning, manualStop, spinComplete, stopReels, isFreeSpinMode]);

  return {
    reelRefs,
    handleSpin,
    handleManualStop,
    remainingAutoSpins,
  };
};

export default useReelsController;
